import type {
  ContractClientRelation,
  ContractParty,
  ContractPartyCollaborationState,
} from "@closer/shared";
import {
  CONTRACT_PARTY_COLLABORATION_NAMES,
  CONTRACT_PARTY_COLLABORATION_NONE,
  CONTRACT_PARTY_ENTITY_TYPE_UNKNOWN,
  CONTRACT_PARTY_POSITION_NAMES,
  CONTRACT_PARTY_ROLE_NAMES,
  effectivePartyPosition,
  findPermission,
  partyDescriptor,
} from "@closer/shared";
import {
  Badge,
  type BadgeProps,
  Button,
  Card,
  Modal,
  Select,
  Skeleton,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

type PrincipalResolution =
  | {
      state: "resolved";
      source: "company" | "client" | "self" | "client_workspace";
      entityType: "person" | "organization";
      displayName: string;
    }
  | { state: "needs_client_selection" }
  | {
      state: "needs_principal_confirmation";
      suggestedClient: { id: string; displayName: string; entityType: string };
    }
  | { state: "needs_principal_mode_selection" }
  | { state: "unsupported"; reason: string };

/** Principal'ın hangi ilişkiden geldiği — kullanıcı diline çevrilmiş hâli. */
const PRINCIPAL_KAYNAK_ADLARI: Record<
  Extract<PrincipalResolution, { state: "resolved" }>["source"],
  string
> = {
  company: "Kurum",
  client: "Müvekkil",
  self: "Kendi adınıza",
  client_workspace: "Müvekkil",
};

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";
import { ContractPartyDialog } from "./ContractPartyDialog";
import { PartyInviteDialog } from "./PartyInviteDialog";

/**
 * Taraf listesinde konum etiketi (CLOS-1108).
 *
 * ⚠ YALNIZ BİLGİ EKLİYORSA GÖSTERİLİYOR. Tür zaten safı söylüyorsa ("Karşı taraf") aynı
 * şeyi ikinci kez yazmak listeyi "Karşı taraf · Alıcı · Karşı taraf"a çevirir ve okuyanı
 * iki alanın farklı şeyler söylediğini sanmaya iter. Etiket, ancak konum TÜRDEN
 * ÇIKARILAMIYORSA ya da türün ima ettiğinden FARKLIYSA görünür — yani tam olarak yeni olan
 * bilgi kadar.
 */
/**
 * KART ETİKETİ — KONUM × TÜR (CLOS-1124 / CLOS-1151, rapor 3 §6).
 *
 * Eski kart "(Karşı taraf · Satıcı)" yazıyor, konumu yalnız türden farklıysa ekliyordu —
 * "biz kimiz?" sorusu açıkta kalıyordu (§4.1). Şimdi her kart konumunu söylüyor; tür
 * biliniyorsa yanına yazılıyor, BİLİNMİYORSA uydurulmuyor: rozetle "Tür belirtilmedi".
 */
function kartEtiketi(taraf: Pick<ContractParty, "partyType" | "position" | "entityType">): string {
  if (taraf.entityType) return partyDescriptor(taraf);
  const konum = effectivePartyPosition(taraf);
  return konum ? CONTRACT_PARTY_POSITION_NAMES[konum] : "Konum belirtilmedi";
}

/**
 * Bizim konumumuzdakiler ÖNCE: sözleşmeyi okuyan ilk satırda kendini görmeli (§4.1).
 *
 * ⚠ İKİ KONUM DA SAYILIYOR (CLOS-1267): kanonik `represented` ve eski `workspace`. Sıralama
 * yalnız `workspace` sorarken, kanonik konuma geçen yeni kayıtlarda "biz" satırı listenin
 * ortasına düşüyordu — kural sessizce anlamını yitirmişti.
 */
function bizimTarafimizMi(taraf: ContractParty): boolean {
  const konum = effectivePartyPosition(taraf);
  return konum === "represented" || konum === "workspace";
}

function bizimkilerOnce(taraflar: readonly ContractParty[]): ContractParty[] {
  return [...taraflar].sort((a, b) => Number(bizimTarafimizMi(b)) - Number(bizimTarafimizMi(a)));
}

const ROZET_TONU: Record<ContractPartyCollaborationState, NonNullable<BadgeProps["tone"]>> = {
  none: "neutral",
  pending: "info",
  active: "success",
  ended: "neutral",
};

/**
 * SÖZLEŞMENİN TARAFLARI (CLOS-111 `[073]` · CLOS-1094 · K1).
 *
 * ### ⚠ Taraf eklemek erişim VERMEZ, davet kabulü hukuki taraf YAPMAZ (R4 §3.1)
 *
 * Bu bölüm sözleşmenin HUKUKİ taraflarını gösteriyor. Satırdaki ortak çalışma rozeti
 * erişim anlatmıyor; yalnız "bu tarafla Closer üzerinde ortak çalışma kurulmaya çalışıldı
 * mı, kuruldu mu" sorusunu cevaplıyor. Kaynak iki tablo (`contract_org_invitations`
 * bekleyen satırı, `contract_org_participants` aktif satırı); sunucu taraf satırına
 * TÜRETİYOR, kolonda tutmuyor.
 *
 * ### ⚠ "Ortak çalışmaya davet et" dört kapıdan geçiyor
 *
 * `contract.share` + kayıt YAZMA erişimi + davet edilmemiş ya da sonlanmış durum + kendi
 * tarafımız olmaması. Kendi tarafımızı davet etmek sunucuda 409; düğmeyi çizmek
 * kullanıcıyı reddedilecek bir eyleme davet etmek olurdu. Kapı yine SUNUCUDA; ekran
 * yalnız dürüst.
 *
 * ### ⚠ E-posta yoksa düğme KAPALI, gizli değil
 *
 * Davet tarafın iletişim adresine gidiyor (K1). Adres yokken düğmeyi gizleseydik kullanıcı
 * özelliğin var olduğunu hiç öğrenemezdi; kapalı düğme + sebep, eksik olanı eyleme çeviriyor.
 */
/** Dış kapsam metni — katalogdaki gerçek açıklamalar, uydurma etiket yok (Halit S3). */
function kapsamMetni(permissions: readonly string[]): string {
  return permissions.map((k) => findPermission(k)?.description ?? k).join(" · ");
}

export function ContractPartiesSection({
  contractId,
  /**
   * Görüntüleyenin bu KAYITTAKİ yazma erişimi (CLOS-888).
   *
   * ⚠ Varsayılan `true`: alan yoksa yazabilir sayılıyor. Kapı sunucuda ve fail-closed;
   * gereksiz gösterilen düğme 404 verir, gereksiz gizlenen düğme işi tamamen durdurur.
   */
  kayitYazma = true,
}: {
  contractId: string;
  kayitYazma?: boolean;
}) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();
  const [tarafAcik, setTarafAcik] = useState(false);
  /** Düzenlenen taraf; `null` = taraf penceresi EKLEME modunda ya da kapalı (CLOS-1117 · H). */
  /** Çıkarma onayının hedefi; `null` = pencere kapalı. */
  const [cikarHedefi, setCikarHedefi] = useState<ContractParty | null>(null);
  const [duzenleHedefi, setDuzenleHedefi] = useState<ContractParty | null>(null);
  /** Davet penceresinin hedefi; `null` = pencere kapalı. */
  const [davetHedefi, setDavetHedefi] = useState<ContractParty | null>(null);

  /**
   * ⚠ ANAHTAR DETAY SAYFASININ ÖNEKİ ALTINDA (`tenantKey(tenantId, "contract", id, …)`):
   * yaşam döngüsü geçişi bütün bölümleri o önekle geçersiz kılıyor; ayrı bir önek verseydik
   * geçişten sonra taraf listesi bayat kalırdı.
   */
  const anahtar = tenantKey(tenantId, "contract", contractId, "taraflar");

  /**
   * TEMSİL EDİLEN TARAFI SEÇME — kararın TEK yazıcısı (K4/B · Principal Remediation).
   *
   * ### ⚠ Neden ayrı bir kontrol, "Taraf ekle" değil
   *
   * "Taraf ekle" penceresinde `Temsil edilen taraf` konumu var ama o yol `principal_source`
   * YAZMIYOR — yani serbestçe yazılmış bir satır kanonik principal olmuyor (öyle olsaydı
   * konum yeniden hukuki iradenin yerine geçerdi, remediation'ın düzelttiği kusur buydu).
   * Kanonik karar yalnız bu uçtan veriliyor.
   *
   * ### ⚠ Aday listesi SÖZLEŞMEYE BAĞLI müvekkiller
   *
   * Portföy bağı tek başına taraf kararı DEĞİL (K4); ama kullanıcının seçeceği müvekkil de
   * havadan gelmiyor. Liste bağlı müvekkiller, karar kullanıcının açık seçimi.
   */
  const bagliMuvekkiller = useQuery({
    queryKey: tenantKey(tenantId, "contract", contractId, "principal-adaylar"),
    enabled: activeTenantId !== null && can("client.view"),
    queryFn: async () =>
      (await api<ContractClientRelation[]>(`/api/contracts/${contractId}/clients`, { tenantId }))
        .data,
  });

  const principalYaz = useMutation({
    mutationFn: (intent: { mode: "self" } | { mode: "client"; clientId: string }) =>
      api(`/api/contracts/${contractId}/principal`, {
        method: "POST",
        tenantId,
        headers: { "content-type": "application/json" },
        body: JSON.stringify(intent),
      }),
    onSuccess: async () => {
      toast.success("Temsil edilen taraf kaydedildi");
      /** ⚠ Bütün detay öneki geçersiz: karar hem principal satırını hem taraf listesini değiştiriyor. */
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "contract", contractId),
      });
    },
    onError: (hata: unknown) =>
      toast.error(
        hata instanceof ApiRequestError ? hata.message : "Temsil edilen taraf kaydedilemedi",
      ),
  });
  const [principalSecim, setPrincipalSecim] = useState("");
  /**
   * Bileşik davet için dış kapsam presetleri — yalnız davet yetkisi varken ve pencere açıkken.
   *
   * ⚠ TÜRE GÖRE SÜZÜLÜYOR (Mega E2E GAP-002). Uç kiracının BÜTÜN presetlerini döndürüyor;
   * `internal` olanlar iç ekip şablonları ve sunucu onları haricî davette 409 ile
   * reddediyor ("Bu preset haricî organizasyon daveti için tanımlanmamış"). Süzgeç
   * olmadan seçici, seçilebilen ama kabul edilmeyen satırlar gösteriyordu — kullanıcıyı
   * kesin bir reddi almaya göndermek.
   *
   * ⚠ Süzgeç istemcide, çünkü uç genel: katılımcı rolleri YÖNETİM ekranı aynı listeyi
   * bütün türleriyle okuyor. Uca tür parametresi eklemek iki çağıranı da değiştirirdi.
   */
  const presetler = useQuery({
    enabled: tarafAcik && can("contract.share"),
    queryKey: tenantKey(tenantId, "contract-role-presets"),
    queryFn: async () =>
      (
        await api<{ id: string; name: string; participantType: string }[]>(
          "/api/contract-role-presets",
          { tenantId },
        )
      ).data,
  });
  const disKapsamPresetleri = (presetler.data ?? []).filter(
    (p) => p.participantType === "external_organization",
  );
  const taraflar = useQuery({
    queryKey: anahtar,
    queryFn: async () =>
      (
        await api<{ parties: ContractParty[]; missing: string[] }>(
          `/api/contracts/${contractId}/parties`,
          { tenantId },
        )
      ).data,
  });

  /**
   * TEMSİL EDİLEN TARAF — sunucudan (Principal Remediation §14/§32).
   *
   * ⚠ Ekran principal'ı KENDİ HESAPLAMIYOR. Eskiden burada aktif çalışma alanının adı
   * "Bizim tarafımız" diye yazılıyordu; hukuk bürosu bağlamında bu YANLIŞTI — büro
   * sözleşmeyi yönetir, tarafı olmaz. Cevap artık tek kanonik çözümleyiciden geliyor.
   */
  const principal = useQuery({
    queryKey: tenantKey(tenantId, "contract", contractId, "principal"),
    queryFn: async () =>
      (
        await api<{
          resolution: PrincipalResolution;
          party: { id: string; displayName: string } | null;
        }>(`/api/contracts/${contractId}/principal`, { tenantId })
      ).data,
  });

  /** Taraf ekleme (CLOS-111 `[073]`). */
  const tarafEkle = useMutation({
    mutationFn: async (govde: Record<string, unknown>) =>
      (
        await api(`/api/contracts/${contractId}/parties`, {
          method: "POST",
          tenantId,
          body: JSON.stringify(govde),
        })
      ).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: anahtar });
      setTarafAcik(false);
      toast.success("Taraf eklendi");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Taraf eklenemedi");
    },
  });

  /**
   * Taraf düzenleme (CLOS-1117 · yetim uç H).
   *
   * ⚠ `PUT …/parties/:partyId` sunucuda vardı, ekranı yoktu: yanlış seçilen konum ya da
   * yazım hatası ancak tarafı SİLİP yeniden ekleyerek düzeltilebiliyordu — ve o yol
   * tarafın kimlik anahtarını, davet geçmişini ve audit izini koparıyordu. Kapı "Kaldır"
   * ile aynı: `contract.edit` + kayıt yazma erişimi (uç da onları istiyor).
   */
  /** Taraf + davet AYNI istek/transaction (CLOS-1125 · 1156). */
  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();
  const tarafEkleVeDavet = useMutation({
    mutationFn: async (girdi: {
      govde: Record<string, unknown>;
      kapsam: { presetId?: string; permissions?: string[] };
    }) =>
      (
        await api(`/api/contracts/${contractId}/parties/invite`, {
          method: "POST",
          tenantId,
          headers: { "idempotency-key": idempotency.uret() },
          body: JSON.stringify({ ...girdi.govde, ...girdi.kapsam }),
        })
      ).data,
    onSuccess: async () => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: anahtar }),
        queryClient.invalidateQueries({
          queryKey: tenantKey(tenantId, "contract", contractId, "org-davetleri"),
        }),
        queryClient.invalidateQueries({
          queryKey: tenantKey(tenantId, "contract", contractId, "paylasim-baglami"),
        }),
      ]);
      setTarafAcik(false);
      toast.success("Taraf eklendi ve davet gönderildi");
    },
    onError: (error) => {
      toast.error(
        error instanceof ApiRequestError ? error.message : "Taraf eklenip davet gönderilemedi",
      );
    },
  });
  /** Bekleyen daveti YENİDEN GÖNDER (aynı davet, yeni token — INV-AT-09). */
  const davetYenile = useMutation({
    mutationFn: async (invitationId: string) =>
      api(`/api/contracts/${contractId}/org-invitations/${invitationId}/renew`, {
        method: "POST",
        tenantId,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: anahtar });
      toast.success("Davet yeniden gönderildi");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Davet yeniden gönderilemedi");
    },
  });
  /**
   * ERİŞİMİ GERİ ÇEK (CLOS-1125 · Guard B): katılım biter; kabul eden + karşı kurumun
   * dağıttığı herkesin erişimi tek işlemde kapanır. Davet ve audit geçmişi durur.
   */
  const katilimSonlandir = useMutation({
    mutationFn: async (participantId: string) =>
      api(`/api/contracts/${contractId}/org-participants/${participantId}/end`, {
        method: "POST",
        tenantId,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: anahtar }),
        queryClient.invalidateQueries({
          queryKey: tenantKey(tenantId, "contract", contractId, "org-davetleri"),
        }),
        queryClient.invalidateQueries({
          queryKey: tenantKey(tenantId, "contract", contractId, "paylasim-baglami"),
        }),
      ]);
      toast.success("Erişim geri çekildi");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Erişim geri çekilemedi");
    },
  });
  const tarafDuzenle = useMutation({
    mutationFn: async (girdi: { partyId: string; govde: Record<string, unknown> }) =>
      (
        await api(`/api/contracts/${contractId}/parties/${girdi.partyId}`, {
          method: "PUT",
          tenantId,
          body: JSON.stringify(girdi.govde),
        })
      ).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: anahtar });
      setDuzenleHedefi(null);
      toast.success("Taraf güncellendi");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Taraf güncellenemedi");
    },
  });

  /**
   * Taraf kaldırma (CLOS-111 `[073]`).
   *
   * ⚠ ONAY PENCERESİ VAR — eski "her silmeye onay koymak uyarıyı değersizleştirir" gerekçesi
   * canlıda ölçülerek çürüdü (Mega E2E BUG-018 / TAR-02): imza bekleyen bir sözleşmede tek
   * tıkla taraf çıkarıldı ve KAPANMIŞ imza süreçleri "Henüz imzacı belirlenmedi"ye döndü.
   * İşlem artık geçmişi bozmuyor (sunucu supersede ediyor, silmiyor) ama tarafın sözleşmedeki
   * katılımını SONLANDIRIYOR; kullanıcı ne olacağını görmeden onaylamamalı.
   */
  const tarafKaldir = useMutation({
    mutationFn: async (partyId: string) =>
      (
        await api(`/api/contracts/${contractId}/parties/${partyId}`, {
          method: "DELETE",
          tenantId,
        })
      ).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: anahtar });
      toast.success("Taraf kaldırıldı");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Taraf kaldırılamadı");
    },
  });

  /**
   * Bekleyen daveti İPTAL (CLOS-245 `[131]`) — aynı uç paylaşım bölümündeki listeyle ortak.
   *
   * ⚠ Üç sorgu birden tazeleniyor: taraf rozeti, paylaşım bölümündeki davet listesi ve
   * paylaşım bağlamı aynı daveti üç açıdan gösteriyor.
   */
  const davetIptal = useMutation({
    mutationFn: async (invitationId: string) =>
      api(`/api/contracts/${contractId}/org-invitations/${invitationId}/cancel`, {
        method: "POST",
        tenantId,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: anahtar }),
        queryClient.invalidateQueries({
          queryKey: tenantKey(tenantId, "contract", contractId, "org-davetleri"),
        }),
        queryClient.invalidateQueries({
          queryKey: tenantKey(tenantId, "contract", contractId, "paylasim-baglami"),
        }),
      ]);
      toast.success("Davet iptal edildi");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Davet iptal edilemedi");
    },
  });

  const duzenleyebilir = can("contract.edit") && kayitYazma;
  const davetEdebilir = can("contract.share") && kayitYazma;
  const bos = taraflar.data?.parties.length === 0;
  const sirali = bizimkilerOnce(taraflar.data?.parties ?? []);

  return (
    <Card>
      <div className="mb-3 flex flex-col gap-1">
        <div className="flex items-center gap-2">
          {/*
            ⚠ "Taraflar" tek başına yetmiyordu (rapor 12. bölüm): kullanıcı bu bölümü
            erişim listesi sanıyordu. Başlık ve açıklama raporun metniyle BİREBİR.
          */}
          <h2 className="mr-auto text-base font-semibold text-fg-heading">Sözleşmenin Tarafları</h2>
          {duzenleyebilir ? (
            <Button type="button" size="sm" variant="secondary" onClick={() => setTarafAcik(true)}>
              Taraf ekle
            </Button>
          ) : null}
        </div>
        {/* Açıklama BAŞLIĞIN ALTINDA ve hata durumundan ÖNCE: bölüm düşse bile ne olduğu okunsun. */}
        <p className="text-sm text-fg-muted">
          Bu bölüm sözleşmenin hukuki taraflarını gösterir. Buraya taraf eklemek Closer erişimi
          vermez.
        </p>
      </div>

      {taraflar.isPending ? (
        <Skeleton lines={3} />
      ) : taraflar.error ? (
        <StateView
          state={resolveErrorState(taraflar.error, {
            idempotent: true,
            overrides: {
              error: {
                title: "Sözleşmenin Tarafları yüklenemedi",
                description: "Bölüm şu an getirilemedi.",
              },
            },
          })}
        />
      ) : (
        <>
          {/*
            ⚠ "BİZİM TARAFIMIZ" HER SÖZLEŞMEDE GÖRÜNÜR (rapor 3 §4.1): taraf satırı olsun olmasın,
            sözleşmeyi hangi organizasyon adına okuduğumuz ilk satırda yazar. Taraf olarak
            eklenmemişse bu bir uyarıdır, tahmin değil — `missing` listesi de aynı şeyi söyler.
          */}
          {/*
            ⚠ "BİZİM TARAFIMIZ = AKTİF ÇALIŞMA ALANI" KALDIRILDI (Principal Remediation
            §3 BUG-01, §24). Eski satır her sözleşmede aktif alanın adını hukuki taraf gibi
            yazıyordu; hukuk bürosu bağlamında bu yanlış ve kullanıcıyı büroyu kendi
            sözleşmesine taraf yazmaya iten şeydi. Cevap artık sunucudaki çözümleyiciden
            geliyor ve çözülemediğinde TAHMİN EDİLMİYOR, sorulacak soru gösteriliyor.
          */}
          <p className="mb-2 text-sm text-fg" data-testid="temsil-edilen-taraf">
            <span className="font-medium">Temsil edilen taraf:</span>{" "}
            {principal.isPending ? (
              <span className="text-fg-muted">yükleniyor…</span>
            ) : principal.data?.resolution.state === "resolved" ? (
              <>
                {principal.data.resolution.displayName}
                <span className="text-fg-muted">
                  {" · "}
                  {PRINCIPAL_KAYNAK_ADLARI[principal.data.resolution.source]}
                </span>
                {principal.data.party ? null : (
                  <span className="text-fg-muted"> · henüz taraf olarak yazılmadı</span>
                )}
              </>
            ) : principal.data?.resolution.state === "needs_principal_confirmation" ? (
              /*
                ⚠ ADAY, HUKUKİ STATE DEĞİL (K4/B). Sözleşme bir müvekkilin portföyünde
                olabilir ama bu tek başına onu hukuki taraf yapmaz — büro karşı tarafın
                sözleşmesini de takip amacıyla portföyüne alabilir. Bu yüzden "Temsil edilen
                taraf: X" DEMİYORUZ; ad aday olarak sunuluyor, karar kullanıcının.
              */
              <span className="text-fg-muted">
                Henüz seçilmedi · İlişkili müvekkil:{" "}
                <span className="text-fg">
                  {principal.data.resolution.suggestedClient.displayName}
                </span>
              </span>
            ) : principal.data?.resolution.state === "needs_client_selection" ? (
              <span className="text-fg-muted">Bu sözleşme henüz bir müvekkile bağlı değil.</span>
            ) : principal.data?.resolution.state === "needs_principal_mode_selection" ? (
              <span className="text-fg-muted">
                Bu sözleşmeyi kimin adına yönettiğiniz belirlenmedi.
              </span>
            ) : (
              <span className="text-fg-muted">Belirlenemedi</span>
            )}
          </p>
          <PrincipalSecimi
            cozum={principal.data?.resolution}
            adaylar={bagliMuvekkiller.data ?? []}
            secim={principalSecim}
            onSecim={setPrincipalSecim}
            bekliyor={principalYaz.isPending}
            gorunur={can("contract.edit") && kayitYazma}
            onKaydet={(intent) => principalYaz.mutate(intent)}
          />
          {bos ? <p className="text-sm text-fg-muted">Henüz taraf eklenmemiş.</p> : null}
          <ul className="flex flex-col gap-2">
            {sirali.map((t) => {
              /**
               * ⚠ Alanı taşımayan eski bir yanıt "davet edilmedi" sayılıyor — durum kolonda
               * değil, sunucuda türetiliyor; ekran yokluğu arıza saymıyor (expand-contract).
               */
              const isbirligi = t.collaboration ?? CONTRACT_PARTY_COLLABORATION_NONE;
              const davetSunulur =
                davetEdebilir &&
                (isbirligi.state === "none" || isbirligi.state === "ended") &&
                /** ⚠ KENDİ TARAFIMIZI DAVET ETMİYORUZ (CLOS-1267): kanonik `represented`
                 *  konumu eklenince kontrol yalnız eski `workspace`'e bakıyordu ve kendi
                 *  tarafımıza "ortak çalışmaya davet et" düğmesi çıkıyordu. */
                !bizimTarafimizMi(t);
              return (
                <li key={t.id} className="flex flex-col gap-1 text-sm text-fg">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{t.displayName}</span>
                    <span className="text-fg-muted">
                      ({kartEtiketi(t)} · {CONTRACT_PARTY_ROLE_NAMES[t.role]})
                    </span>
                    {t.entityType === null ? (
                      <Badge tone="warning">{CONTRACT_PARTY_ENTITY_TYPE_UNKNOWN}</Badge>
                    ) : null}
                    <Badge tone={ROZET_TONU[isbirligi.state]}>
                      {CONTRACT_PARTY_COLLABORATION_NAMES[isbirligi.state]}
                    </Badge>
                    {duzenleyebilir ? (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setDuzenleHedefi(t)}
                        >
                          Düzenle
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setCikarHedefi(t)}
                        >
                          Kaldır
                        </Button>
                      </>
                    ) : null}
                  </div>

                  {isbirligi.state === "pending" ? (
                    <div className="flex flex-wrap items-center gap-2 text-xs text-fg-soft">
                      <span>
                        Davet gönderildi
                        {isbirligi.targetEmail ? `: ${isbirligi.targetEmail}` : ""}
                      </span>
                      {isbirligi.permissions.length > 0 ? (
                        <span data-testid={`taraf-kapsam-${t.id}`}>
                          · Kapsam: {kapsamMetni(isbirligi.permissions)}
                        </span>
                      ) : null}
                      {davetEdebilir && isbirligi.invitationId ? (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => davetYenile.mutate(isbirligi.invitationId!)}
                            loading={davetYenile.isPending}
                          >
                            Yeniden gönder
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => davetIptal.mutate(isbirligi.invitationId!)}
                            loading={davetIptal.isPending}
                          >
                            İptal et
                          </Button>
                        </>
                      ) : null}
                    </div>
                  ) : isbirligi.state === "active" && isbirligi.linkedTenantName ? (
                    <div className="flex flex-col gap-1 text-xs text-fg-soft">
                      <span className="flex flex-wrap items-center gap-2">
                        <span>
                          Ortak çalışıyor: {isbirligi.linkedTenantName}
                          {isbirligi.permissions.length > 0
                            ? ` · Kapsam: ${kapsamMetni(isbirligi.permissions)}`
                            : ""}
                        </span>
                        {davetEdebilir && isbirligi.participantId ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => katilimSonlandir.mutate(isbirligi.participantId!)}
                            loading={katilimSonlandir.isPending}
                          >
                            Erişimi geri çek
                          </Button>
                        ) : null}
                      </span>
                      {/*
                        ⚠ YALNIZ ERİŞİMLİ ADLAR (CLOS-1125 / Halit S4): karşı kurumun bu
                        sözleşmeye erişim verdiği kişiler; üye dizini değil. Görünürlük var,
                        YÖNETİM YOK — rol/kaldırma/yeni kişi karşı kurumun kendi ekranında.
                      */}
                      {isbirligi.authorizedUsers.length > 0 ? (
                        <span data-testid={`taraf-yetkililer-${t.id}`}>
                          Yetkililer:{" "}
                          {isbirligi.authorizedUsers.map((u) => `${u.name} — Aktif`).join(", ")}
                        </span>
                      ) : null}
                    </div>
                  ) : null}

                  {davetSunulur ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={!t.email}
                        onClick={() => setDavetHedefi(t)}
                      >
                        Ortak çalışmaya davet et
                      </Button>
                      {!t.email ? (
                        <span className="text-xs text-fg-soft">
                          Davet için bir iletişim e-postası ekleyin
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {/* Eksik taraf yapısı bir UYARI: sözleşme en az iki taraf ve bir çalışma alanı ister. */}
          {(taraflar.data?.missing ?? []).map((eksik) => (
            <p key={eksik} className="pt-2 text-sm text-warning">
              {eksik}
            </p>
          ))}
        </>
      )}

      {/*
        TARAF ÇIKARMA ONAYI (Mega E2E BUG-018 / TAR-02).

        ⚠ Ne olacağı AÇIKÇA yazılı: kullanıcı "Kaldır" kelimesinden kaydın silineceğini
        anlıyordu. Sunucu artık supersede ediyor — imza ve müzakere geçmişi yerinde kalıyor,
        taraf yalnız güncel listeden düşüyor.
      */}
      {cikarHedefi ? (
        <Modal
          open
          onClose={() => setCikarHedefi(null)}
          title={`${cikarHedefi.displayName} sözleşmeden çıkarılsın mı?`}
        >
          <div className="flex flex-col gap-3 text-sm text-fg-soft">
            <p>
              Kayıt silinmez: bu tarafa bağlı imza, mutabakat ve müzakere geçmişi olduğu gibi
              korunur.
            </p>
            <p>Taraf güncel taraf listesinden düşer ve onun adına yeni işlem yapılamaz.</p>
            <p>Gerekirse aynı taraf sonradan yeniden eklenebilir.</p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setCikarHedefi(null)}>
                Vazgeç
              </Button>
              <Button
                type="button"
                loading={tarafKaldir.isPending}
                onClick={() => {
                  const hedef = cikarHedefi.id;
                  setCikarHedefi(null);
                  tarafKaldir.mutate(hedef);
                }}
              >
                Çıkar
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {/* Tek pencere iki modda: `duzenleHedefi` doluysa düzenleme, değilse ekleme. */}
      <ContractPartyDialog
        open={tarafAcik || duzenleHedefi !== null}
        mevcut={duzenleHedefi}
        onClose={() => {
          setTarafAcik(false);
          setDuzenleHedefi(null);
        }}
        onConfirm={(govde) =>
          duzenleHedefi
            ? tarafDuzenle.mutate({ partyId: duzenleHedefi.id, govde })
            : tarafEkle.mutate(govde)
        }
        onConfirmAndInvite={(govde, kapsam) => tarafEkleVeDavet.mutate({ govde, kapsam })}
        davetEdebilir={davetEdebilir}
        presetler={disKapsamPresetleri}
        isPending={tarafEkle.isPending || tarafDuzenle.isPending || tarafEkleVeDavet.isPending}
      />
      <PartyInviteDialog
        open={davetHedefi !== null}
        onClose={() => setDavetHedefi(null)}
        contractId={contractId}
        taraf={davetHedefi}
      />
    </Card>
  );
}

/**
 * TEMSİL EDİLEN TARAF SEÇİCİSİ.
 *
 * ### ⚠ OTOMATİK BAĞLAMDA SEÇİM YOK
 *
 * Kurum ve Müvekkil Alanı bağlamlarında principal bağlamdan belirleniyor; sunucu o iki
 * kaynakta seçim denemesine 409 veriyor. Kontrolü yine de çizseydik kullanıcıya
 * yapamayacağı bir işi teklif etmiş olurduk. Karar kullanıcının olduğu bağlamlar
 * hukuk bürosu ve bireysel kullanıcı.
 *
 * ### ⚠ "Kendi adıma" YALNIZ BİREYSELDE
 *
 * Hukuk bürosu kendi adına sözleşme yapmıyor; sunucu `mode:"self"`'i orada 409'la
 * reddediyor. Seçenek bu yüzden yalnız sunucunun bu modu beklediği iki durumda çiziliyor:
 * mod seçimi istendiğinde ve zaten kendi adına çözülmüşken (değiştirip geri dönebilmek için).
 */
function PrincipalSecimi({
  cozum,
  adaylar,
  secim,
  onSecim,
  bekliyor,
  gorunur,
  onKaydet,
}: {
  cozum: PrincipalResolution | undefined;
  adaylar: ContractClientRelation[];
  secim: string;
  onSecim: (v: string) => void;
  bekliyor: boolean;
  gorunur: boolean;
  onKaydet: (intent: { mode: "self" } | { mode: "client"; clientId: string }) => void;
}) {
  if (!gorunur || !cozum) return null;
  if (cozum.state === "unsupported") return null;
  /** ⚠ Bağlamdan belirlenen principal seçilemez — sunucu da 409 veriyor. */
  if (
    cozum.state === "resolved" &&
    (cozum.source === "company" || cozum.source === "client_workspace")
  )
    return null;

  const kendiAdinaSunulur =
    cozum.state === "needs_principal_mode_selection" ||
    (cozum.state === "resolved" && cozum.source === "self");

  if (adaylar.length === 0 && !kendiAdinaSunulur) {
    /** ⚠ Aday yokken boş bir seçim kutusu çizmek yerine eksik olan adımı söylüyoruz. */
    return (
      <p className="mb-3 text-xs text-fg-soft" data-testid="principal-aday-yok">
        Temsil edilen tarafı seçebilmek için önce sözleşmeyi bir müvekkile bağlayın.
      </p>
    );
  }

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="principal-secimi">
      <Select
        aria-label="Temsil edilen taraf"
        data-testid="principal-secim"
        value={secim}
        onChange={(o) => onSecim(o.target.value)}
      >
        <option value="">Seçiniz…</option>
        {kendiAdinaSunulur ? <option value="self">Kendi adıma</option> : null}
        {adaylar.map((a) => (
          <option key={a.clientId} value={a.clientId}>
            {a.clientDisplayName}
          </option>
        ))}
      </Select>
      <Button
        type="button"
        size="sm"
        data-testid="principal-kaydet"
        loading={bekliyor}
        disabled={secim.length === 0}
        onClick={() =>
          onKaydet(secim === "self" ? { mode: "self" } : { mode: "client", clientId: secim })
        }
      >
        {cozum.state === "resolved" ? "Değiştir" : "Temsil edilen taraf yap"}
      </Button>
    </div>
  );
}
